import { AlertTriangle, ArrowLeft, ArrowRight, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckboxField } from "@/shared/components/forms/ColocaCheckboxField";
import { ColocaChoiceCardField } from "@/shared/components/forms/ColocaChoiceCardField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { compareDecimalStrings, trimTrailingZeros } from "@/shared/utils/decimal";
import type { PaymentTaxBase } from "@/shared/utils/paymentTaxes";
import { formatRail } from "@/shared/utils/railDisplay";

import {
  countryLabel,
  currencyOfCountry,
  railsOfCountry,
  TAX_COUNTRIES,
} from "../../constants/paymentTaxes.constants";
import { useTaxMutations } from "../../hooks/useTaxMutations";
import type { PaymentTax } from "../../types/paymentTaxes.types";
import { TaxExample } from "./TaxExample";
import {
  ANY_RAIL,
  buildTaxFormSchema,
  seedTaxForm,
  TAX_STEP_FIELDS,
  type TaxFormValues,
  toCreateTaxPayload,
} from "./taxFormSchema";

export interface EditTaxIntent {
  /** The version the form starts from; null for a tax that does not exist yet. */
  source: PaymentTax | null;
  /** Locks country, code, base, rail and direction: a new version of that exact scope. */
  lockScope: boolean;
  /** Where the version is written: null = the general tax, otherwise that organization's own. */
  organizationId: string | null;
  organizationName?: string;
  /** In force at that scope today — the "before" of the review. */
  current: PaymentTax | null;
}

export interface EditTaxDialogProps {
  intent: EditTaxIntent;
  onClose: () => void;
  onSaved: (tax: PaymentTax) => void;
}

type Step = "form" | "review";

// The scope comes from where the dialog was opened, never from a field.
export function EditTaxDialog({ intent, onClose, onSaved }: EditTaxDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const { isSubmitting, error, createTax } = useTaxMutations();
  const { source, lockScope, organizationId, organizationName, current } = intent;
  const requiresAck = organizationId === null;
  const [step, setStep] = useState<Step>("form");

  const schema = useMemo(
    () =>
      buildTaxFormSchema({
        code: t("validation_code"),
        name: t("validation_name"),
        rate: t("validation_rate"),
        rateGross: t("validation_rate_gross"),
        validFrom: t("validation_valid_from"),
        reason: t("validation_reason"),
        acknowledge: t("validation_ack"),
      }),
    [t],
  );
  const form = useZodForm(schema, seedTaxForm(source, { country: TAX_COUNTRIES[0].iso3, requiresAck }));
  const values = useWatch({ control: form.control }) as TaxFormValues;
  const currency = currencyOfCountry(values.country);

  useEffect(() => {
    if (values.rail === ANY_RAIL || lockScope) return;
    if (!railsOfCountry(values.country).some((rail) => rail === values.rail)) {
      form.setValue("rail", ANY_RAIL);
    }
  }, [form, lockScope, values.country, values.rail]);

  const title = !source
    ? t("dialog_title_new")
    : organizationId && !current?.organization_id
      ? t("dialog_title_special", { name: source.name })
      : t("dialog_title_change", { name: source.name });

  const goToReview = async () => {
    if (await form.trigger([...TAX_STEP_FIELDS])) setStep("review");
  };

  const onSubmit = async (submitted: TaxFormValues) => {
    if (requiresAck && step === "form") {
      await goToReview();
      return;
    }
    const created = await createTax(toCreateTaxPayload(submitted, organizationId));
    if (created) onSaved(created);
  };

  const baseItems = (["COMMISSION", "GROSS_AMOUNT"] as PaymentTaxBase[]).map((base) => ({
    value: base,
    label: t(base === "COMMISSION" ? "base_commission" : "base_gross"),
    description: (
      <TaxExample base={base} name={values.name ?? ""} rate={values.rate || "0"} currency={currency} />
    ),
  }));

  const rateLabel = (tax: PaymentTax | null) =>
    !tax
      ? t("review_none")
      : compareDecimalStrings(tax.rate, "0") === 0
        ? t("exempt")
        : `${trimTrailingZeros(tax.rate)} % · ${t(tax.bearer === "CLIENT" ? "bearer_client" : "bearer_coloca")}`;

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={title}
      description={
        organizationId
          ? t("dialog_scope_org", { org: organizationName ?? organizationId })
          : t("dialog_scope_general")
      }
    >
      <ColocaForm form={form} onSubmit={onSubmit} className="space-y-4">
        {step === "form" ? (
          <>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <ColocaSelectField
                name="country"
                label={t("field_country")}
                disabled={lockScope}
                items={TAX_COUNTRIES.map((country) => ({
                  value: country.iso3,
                  label: countryLabel(country.iso3, activeLanguage),
                }))}
              />
              <ColocaInputField
                name="code"
                label={t("field_code")}
                description={t("field_code_hint")}
                disabled={lockScope}
                maxLength={8}
                transform={(raw) => raw.toUpperCase()}
              />
            </div>
            <ColocaInputField
              name="name"
              label={t("field_name")}
              description={t("field_name_hint")}
              maxLength={64}
            />
            <ColocaChoiceCardField
              name="base"
              label={t("field_base")}
              description={lockScope ? t("field_base_locked") : undefined}
              columns={2}
              disabled={lockScope}
              items={baseItems}
            />
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <ColocaAmountInput
                name="rate"
                label={t("field_rate")}
                description={t(values.base === "GROSS_AMOUNT" ? "field_rate_gross_hint" : "field_rate_hint")}
                suffix="%"
                maxFractionDigits={4}
              />
              <ColocaRadioGroupField
                name="bearer"
                label={t("field_bearer")}
                description={t(values.bearer === "COLOCA" ? "bearer_coloca_hint" : "bearer_client_hint")}
                items={[
                  { value: "CLIENT", label: t("bearer_client") },
                  { value: "COLOCA", label: t("bearer_coloca") },
                ]}
              />
            </div>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <ColocaRadioGroupField
                name="direction"
                label={t("field_direction")}
                disabled={lockScope}
                items={[
                  { value: "BOTH", label: t("where_all") },
                  { value: "PAYIN", label: t("where_payin") },
                  { value: "PAYOUT", label: t("where_payout") },
                ]}
              />
              <ColocaSelectField
                name="rail"
                label={t("field_rail")}
                disabled={lockScope}
                items={[
                  { value: ANY_RAIL, label: t("field_rail_any") },
                  ...railsOfCountry(values.country).map((rail) => ({ value: rail, label: formatRail(rail) })),
                ]}
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-2.5 text-sm font-medium">
                <ColocaSwitch
                  checked={values.effectiveNow ?? true}
                  onCheckedChange={(checked) => form.setValue("effectiveNow", checked)}
                  aria-label={t("eff_now_label")}
                />
                {t("eff_now_label")}
              </label>
              {!values.effectiveNow && (
                <div className="flex flex-wrap items-start gap-2">
                  <div className="w-40">
                    <ColocaInputField name="validFromDate" type="date" label={t("eff_date_label")} />
                  </div>
                  <div className="w-32">
                    <ColocaInputField name="validFromTime" type="time" label={t("eff_time_label")} />
                  </div>
                </div>
              )}
            </div>

            <ColocaTextareaField
              name="reason"
              label={t("reason_label")}
              placeholder={t("reason_placeholder")}
              description={t("reason_hint")}
              maxLength={500}
              rows={2}
            />
          </>
        ) : (
          <>
            <div className="space-y-3 rounded-xl border border-border bg-card p-4">
              <p className="text-sm font-semibold">{t("review_title")}</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-muted-foreground">{t("review_before")}</dt>
                <dd className="font-mono tabular-nums">{rateLabel(current)}</dd>
                <dt className="text-muted-foreground">{t("review_after")}</dt>
                <dd className="font-mono font-semibold tabular-nums">
                  {compareDecimalStrings(values.rate || "0", "0") === 0
                    ? t("exempt")
                    : `${trimTrailingZeros(values.rate)} % · ${t(values.bearer === "COLOCA" ? "bearer_coloca" : "bearer_client")}`}
                </dd>
              </dl>
              <TaxExample base={values.base} name={values.name} rate={values.rate} currency={currency} />
            </div>
            <ColocaAlert
              variant="warning"
              icon={AlertTriangle}
              text={t("review_affects", {
                name: values.name,
                country: countryLabel(values.country, activeLanguage),
              })}
            />
            <ColocaTextareaField
              name="reason"
              label={t("reason_label")}
              placeholder={t("reason_placeholder")}
              maxLength={500}
              rows={2}
            />
            <ColocaCheckboxField name="acknowledged" label={t("review_ack")} />
          </>
        )}

        {error && <ColocaAlert variant="destructive" text={error} />}

        <div className="flex justify-end gap-2 pt-2">
          {step === "review" ? (
            <ColocaButton
              type="button"
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => setStep("form")}
              disabled={isSubmitting}
            >
              {t("button_back")}
            </ColocaButton>
          ) : (
            <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {t("button_cancel")}
            </ColocaButton>
          )}
          {requiresAck && step === "form" ? (
            <ColocaButton
              key="review"
              type="button"
              variant="primary"
              icon={ArrowRight}
              onClick={() => void goToReview()}
            >
              {t("button_review")}
            </ColocaButton>
          ) : (
            <ColocaButton key="save" type="submit" variant="primary" icon={Save} loading={isSubmitting}>
              {t("button_save")}
            </ColocaButton>
          )}
        </div>
      </ColocaForm>
    </ColocaDialog>
  );
}
